<script lang="ts" setup>
import { useKvStore } from '~~/stores/KvStore'


// State for alert dismissal and modal
const alertDismissed = ref(false)
const modalOpen = ref(false)

// Get the KV store
const kvStore = useKvStore()

// State for the EOL notice
const showEolNotice = ref(false)

// Load EOL notice settings on component mount
onMounted(async () => {
  // Wait for the KV store to load data and check the EOL settings
  const shouldShowNotice = await kvStore.getValue('eol_notice')
  showEolNotice.value = shouldShowNotice === 'true'
})

// Set up a watcher to react to realtime changes
watch(
  () => kvStore.getValueSync('eol_notice'),
  (newValue) => {
    if (newValue !== null) {
      showEolNotice.value = newValue === 'true'
    }
  }
)
</script>

<template>
  <UAlert
    v-if="showEolNotice && !alertDismissed"
    title="End of Life Notice"
    :description="`This application will no longer receive data updates after April 18th, 2025.`"
    icon="i-material-symbols-support"
    color="error"
    variant="soft"
    class="rounded-none"
    orientation="horizontal"
    :ui="{ icon: 'size-12'}"
    @update:open="alertDismissed = true"
    :actions="[
      {
        label: 'Learn More',
        color: 'error',
        variant: 'solid',
        icon: 'i-material-symbols-info',
        onClick: () => {
          modalOpen = true
        }
      },
      {
        label: 'Dismiss',
        color: 'warning',
        variant: 'solid',
        icon: 'i-heroicons-x-mark',
        onClick: () => {
          alertDismissed = true
        }
      }
    ]"
  />
  <UModal v-model:open="modalOpen">
    <template #title>
      <h2 class="font-semibold text-2xl text-(--ui-error)">
        End of Life Notice
      </h2>
    </template>
    <template #body>
      <h2 class="font-semibold text-lg">
        Thank You!
      </h2>
      <div class="flex flex-col gap-3">
        <p>
          ...to everyone who has used, contributed to, and provided feedback for this application.
        </p>
        <p>
          I hope PCR has been helpful in your day to day workflows and provided some value. My last day at the company is April 18th, 2025,
          after which my email will be closed and the data fueling this application will no longer be updated.
        </p>
        <p>
          Before then, please use the CSV export feature for your data needs or reach out to me directly for larger datasets.
        </p>
        <p>
          Working with such a talented and dedicated team has been an incredible learning experience, and I'm truly grateful for this opportunity.
          I truly hope that I was able to provide a fraction of the value that you all have given me.
        </p>
      </div>
      <p class="font-['Mrs_Saint_Delafield'] text-4xl text-center mt-6">
        Tony Costanzo
      </p>
      <div class="flex flex-col w-full justify-center items-center gap-1 mt-4">
        <UButton to="tel:12105511095" color="tertiary" variant="soft" class="text-center" icon="i-heroicons-phone-arrow-up-right">
            210-551-1095
        </UButton>
        <UButton to="mailto:tonycos92@gmail.com" color="primary" variant="soft" class="text-center" icon="i-heroicons-envelope">
            tonycos92@gmail.com
        </UButton>
        <UButton to="https://www.linkedin.com/in/tonycos/" target="_blank" color="info" variant="soft" class="text-center" icon="i-heroicons-link">
            LinkedIn
        </UButton>
        <UButton to="https://tony-costanzo.com" target="_blank" color="info" variant="soft" class="text-center" icon="i-heroicons-globe-alt">
            Portfolio
        </UButton>
      </div>
    </template>
  </UModal>
</template>